/**
 * speech — «Анализ речи»: перебивания (счёт, разбивка оператор/клиент, балл),
 * слова-паразиты (счёт, топ-слова, балл), неуверенность агента (счёт,
 * топ-слова, балл; в данных поле называется `stopWords`), запрещённые слова
 * проекта (счёт, произнесённая форма, говорящий, запись списка; без балла),
 * обязательные фразы (использованные / пропущенные / балл), ненормативная
 * лексика (обнаружена → фраза ×N, не обнаружена → заглушка). Ошибки оператора
 * (`speech.operatorErrors`) сюда НЕ входят — бриф задачи 9 явно выносит их в
 * ключевые моменты (задача 10).
 *
 * Перебивания читаются из `result.emotional.interruptions` (не `speech`),
 * но печатаются здесь, а не в emotional.ts — это ИСПРАВЛЕНИЕ (не
 * первоначальное решение задачи 9): на экране v2 их показывает именно
 * `SpeechSection.tsx:77,118-138` («Анализ речи»), `EmotionalSection.tsx` их
 * не показывает вовсе. Изначально задача положила их в «Вежливость» по
 * прямому, но ошибочному указанию — команда проверила экран построчно и
 * поправила. Строка идёт ПЕРВОЙ, как на экране (сразу после соотношения
 * речи, которое печать не воспроизводит — данных `diarization`/`agentName`
 * нет в `PrintReportInput`).
 *
 * Топ-слова паразитов/неуверенности и фразы профанити печатаются построчным
 * текстом ««слово» ×N» через запятую (согласованный прототип,
 * preview.html:277-278), а не чипами, как на экране (`<Tag>` в
 * SpeechSection.tsx) — тот же класс расхождения «вёрстка меняется, данные
 * нет», что и в dialogue.ts/recommendations.ts: печатная строка компактнее
 * блока чипов на печатной странице фиксированной ширины.
 *
 * Балл неуверенности ПЕЧАТАЕТСЯ, хотя бриф задачи 9 упоминает балл только для
 * паразитов, а согласованный прототип (preview.html:278) эту строку без балла
 * рисует. Экран (SpeechSection.tsx: `stopWords.score !== undefined`) показывает
 * балл точно так же, как балл паразитов — это разошлось с брифом/прототипом, а
 * не с экраном, и по правилу задачи («прав экран») решено в пользу экрана.
 *
 * Обязательные фразы — те же данные (`speech.mandatoryPhrases`), что печатает
 * ComplianceSection.tsx на экране, но в ДРУГОЙ карточке: экран кладёт их в
 * «Комплаенс», согласованный прототип (preview.html:279) — в «Анализ речи».
 * Бриф задачи 9 явно перечисляет их для `renderSpeech`, гейт/приоритет
 * `missingCodes` → `missing` — та же логика, что в ComplianceSection.tsx.
 * Фолбэк непереведённого кода — через ИМПОРТИРОВАННУЮ `mandatoryPhraseCodeLabel`
 * (не копию): она при отсутствии перевода даёт человекочитаемую английскую
 * фразу (`FALLBACK_LABELS`), а не голый код капсом посреди локализованного
 * документа. Копия этой карты неизбежно разошлась бы с оригиналом при
 * добавлении пятого кода — тот же класс расхождения, что `SCORE_MAP`/
 * `buildBantRows`/`confidenceLevelPercent` в других секциях, чинится тем же
 * способом: импортом, а не копией. Сигнатура функции требует `i18next.
 * TFunction`, печатный `t` типизирован уже (`TFn`) — на границе вызова
 * приведение типа (`as unknown as TFunction`), не переопределение всей
 * функции: на рантайме это тот же `t` (namespace-префикс `records:` в
 * функции резолвится i18next независимо от намespace, на который уже
 * забинжен `t`).
 *
 * Печать здесь мягче экрана в двух местах, и это осознанно. Экран
 * (`SpeechSection.tsx`: `if (!result.speech) return null`) не рисует
 * карточку речи без блока `speech` — а значит не покажет перебивания
 * (`result.emotional.interruptions`) в одиночку, даже если они есть.
 * Экран (`ComplianceSection.tsx`: `if (!result.compliance) return null`)
 * так же не рисует комплаенс-карточку без блока `compliance` — а значит не
 * покажет облако обязательных фраз (`result.speech.mandatoryPhrases`), даже
 * если оно есть. `renderSpeech` читает оба поля напрямую и гейтится только
 * по факту наличия данных, поэтому печатает и то, и другое там, где экран
 * промолчал бы. Документ читают вне приложения — прятать реально
 * посчитанные данные из-за отсутствующего соседнего блока смысла не имеет.
 *
 * У организации с Э2 встроенных (#2518) строка мата печатается целиком только
 * при результате `agent_profanity` по старому полю (иначе — реплики клиента),
 * а строка обязательных фраз
 * фильтруется по кодам — то же правило, что на экране
 * (`lib/records/builtin-legacy-rows`): иначе встроенный стоит в разделе
 * «Критерии».
 */
import { card, kvRow, tag, scoreSpan, safeNumber } from '../primitives';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';
import { mandatoryPhraseCodeLabel } from '@/lib/mandatoryPhraseCodeLabel';
import {
  legacyMandatoryPhrases,
  legacyProfanity,
  pausedByAeloPhraseCodes,
} from '@/lib/records/builtin-legacy-rows';
import type { TFunction } from 'i18next';

function wordListText(words: Array<{ word: string; count?: number }>): string {
  // `w.count` — приведение типа, не гарантия (см. `safeNumber` в
  // primitives.ts); нечисловое значение опускается, а не подставляется.
  return words
    .map((w) => {
      const count = safeNumber(w.count);
      return `«${escapeHtml(w.word)}»${count !== null ? ` ×${count}` : ''}`;
    })
    .join(', ');
}

export const renderSpeech: SectionFn = (input, t) => {
  const speech = input.analysis.result.speech;
  const interruptions = input.analysis.result.emotional?.interruptions;

  // Порядок строки и формат («count breakdown score») — буквально
  // SpeechSection.tsx: `interruptionsBreakdown` — тот же интерполируемый
  // ключ (`{{agent}}`/`{{client}}`), балл — та же необязательная плашка.
  const interruptionsHtml = interruptions
    ? kvRow(
        t('records.analysisPanel.interruptions'),
        escapeHtml(
          `${interruptions.count} ${t('records.analysisPanel.interruptionsBreakdown', {
            agent: interruptions.byAgent,
            client: interruptions.byClient,
          })}`
        ) +
          (interruptions.score !== undefined ? ` ${scoreSpan(interruptions.score)}` : '')
      )
    : '';

  // Печатается и при нуле — ровно как на экране (SpeechSection.tsx): метрика,
  // исчезающая из отчёта, когда ничего не нашла, неотличима от метрики,
  // которая не отработала (ревью релиза 2026-08-15). С #819 счёт — число
  // звуков колебания агента, посчитанное кодом, а список — самые частые формы
  // без числа у каждой; у старых анализов это поле модели и её примеры. Счёт
  // без примеров печатает «N — детали не сохранились», а не прячет строку.
  const fillerWords = speech?.fillerWords;
  const fillerScoreHtml =
    fillerWords?.score !== undefined ? ` ${scoreSpan(fillerWords.score)}` : '';
  const fillerHtml = fillerWords
    ? kvRow(
        t('records.analysisPanel.fillerWords'),
        fillerWords.topWords.length > 0
          ? `${fillerWords.count} <span style="color:var(--fg3)">— ${wordListText(fillerWords.topWords)}</span>${fillerScoreHtml}`
          : `${fillerWords.count} <span style="color:var(--fg3)">— ${escapeHtml(
              fillerWords.count === 0
                ? t('records.analysisPanel.none')
                : t('records.analysisPanel.noDetails', 'no details recorded')
            )}</span>${fillerScoreHtml}`
      )
    : '';

  // Печатается и при нуле — ровно как на экране (SpeechSection.tsx): пустая
  // метрика, исчезающая из отчёта, неотличима от метрики, которая не
  // отработала.
  //
  // Печатается ИМЕННО `count`, а не литеральный ноль, и «Не выявлено»
  // решается по `count === 0` — тем же условием, что на экране. Санитайзер
  // (safety-gates.ts) умеет оставить `{count: 5, topWords: []}`: там экран
  // покажет «(5)», и отчёт, печатавший «0», спорил бы с ним об одной и той
  // же записи.
  const stopWords = speech?.stopWords;
  const stopScoreHtml =
    stopWords?.score !== undefined ? ` ${scoreSpan(stopWords.score)}` : '';
  const stopHtml = stopWords
    ? kvRow(
        t('records.analysisPanel.agentUncertainty'),
        stopWords.topWords.length > 0
          ? `${stopWords.count} <span style="color:var(--fg3)">— ${wordListText(stopWords.topWords)}</span>${stopScoreHtml}`
          : `${stopWords.count} <span style="color:var(--fg3)">— ${escapeHtml(
              stopWords.count === 0
                ? t('records.analysisPanel.none')
                : t('records.analysisPanel.noDetails', 'no details recorded')
            )}</span>${stopScoreHtml}`
      )
    : '';

  const mandatoryPhrases = legacyMandatoryPhrases(
    speech?.mandatoryPhrases,
    input.builtinSources,
    input.builtinPausedByAelo
  );
  const mandatoryUsed = mandatoryPhrases?.used ?? [];
  // Приоритет стабильных кодов над свободным текстом — как на экране
  // (ComplianceSection.tsx): старые записи хранят только `missing`, коды
  // появились позже и не бэкфилились.
  const missingCodes = mandatoryPhrases?.missingCodes;
  const mandatoryMissingLabels =
    missingCodes !== undefined
      ? missingCodes.map((code) =>
          mandatoryPhraseCodeLabel(code, t as unknown as TFunction)
        )
      : (mandatoryPhrases?.missing ?? []);
  const mandatoryHtml =
    mandatoryUsed.length > 0 || mandatoryMissingLabels.length > 0
      ? kvRow(
          t('records.analysisPanel.mandatoryPhrases'),
          (mandatoryPhrases!.score !== undefined
            ? scoreSpan(mandatoryPhrases!.score)
            : '') +
            mandatoryUsed.map((p) => ` ${tag(`✓ ${p}`, 'ok')}`).join('') +
            mandatoryMissingLabels.map((p) => ` ${tag(`✗ ${p}`, 'bad')}`).join('') +
            // Подпись паузы Aelo у кода — как на экране (`ComplianceSection`).
            pausedByAeloPhraseCodes(
              missingCodes,
              input.builtinSources,
              input.builtinPausedByAelo
            )
              .map(
                (code) =>
                  `<div style="color:var(--fg3)">${escapeHtml(
                    t('records.analysisPanel.compliance.pausedByAeloPhrase', {
                      label: mandatoryPhraseCodeLabel(code, t as unknown as TFunction),
                    })
                  )}</div>`
              )
              .join('')
        )
      : '';

  const profanity = legacyProfanity(
    input.analysis.result,
    input.builtinSources,
    input.builtinPausedByAelo
  );
  let profanityHtml = '';
  if (profanity) {
    if (!profanity.detected) {
      profanityHtml = kvRow(
        t('records.analysisPanel.profanity'),
        escapeHtml(t('records.analysisPanel.none'))
      );
    } else {
      const instances = profanity.instances ?? [];
      const instancesText = instances
        .map((inst) => {
          const speakerHtml =
            inst.speaker !== 'unknown'
              ? ` · ${escapeHtml(
                  t(
                    inst.speaker === 'agent'
                      ? 'records.analysisPanel.speakerAgent'
                      : 'records.analysisPanel.speakerClient'
                  )
                )}`
              : '';
          // `inst.count` — приведение типа, не гарантия (см. `safeNumber` в
          // primitives.ts); нечисловое значение опускается, а не подставляется.
          const count = safeNumber(inst.count);
          return `«${escapeHtml(inst.phrase)}»${count !== null ? ` ×${count}` : ''}${speakerHtml}`;
        })
        .join(', ');
      profanityHtml = kvRow(
        t('records.analysisPanel.profanity'),
        escapeHtml(t('records.analysisPanel.detected')) +
          (instancesText
            ? ` <span style="color:var(--fg3)">— ${instancesText}</span>`
            : '')
      );
    }
  }

  if (
    !interruptionsHtml &&
    !fillerHtml &&
    !stopHtml &&
    !mandatoryHtml &&
    !profanityHtml
  ) {
    return '';
  }

  return card({
    title: t('records.analysisPanel.speechAnalysis'),
    body: interruptionsHtml + fillerHtml + stopHtml + mandatoryHtml + profanityHtml,
  });
};
